/// <amd-module name="Tfs/Frame/Components/Profile/Service/MeControlService" />

import { IMeControlData } from "Tfs/Frame/Components/Profile/Contracts/DataModels";
import * as Resources from "Tfs/Frame/Resources";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IContributedCommand, IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IVssThemeService } from "VSS/Platform/Theming";

export interface IMeControlService extends IVssService {
    getConfigurations: (data: IMeControlData, imageUrl: string) => object;
}

class MeControlService extends VssService implements IMeControlService {
    private telemetryService: IVssTelemetryService;

    public getConfigurations(data: IMeControlData, imageUrl: string): object {
        const themeService = this.pageContext.getService<IVssThemeService>("IVssThemeService");
        const currentTheme = themeService.getCurrentTheme();

        return {
            apiGeneration: "GEN2",
            containerId: "me-control-container",
            currentAccount: data.currentAccount.isAnonymous
                ? undefined
                : {
                      type: data.currentAccount.isMsaOrFpmsa === true ? "msaFed" : "aad",
                      memberName: data.currentAccount.email,
                      displayName: data.currentAccount.name,
                      orgName: data.currentAccount.aadTenantDisplayName,
                      pictureUrl: imageUrl,
                      authenticatedState: "signedIn",
                      profile: {
                          profileName: data.currentAccount.name,
                          profilePictureUrl: imageUrl,
                          editProfilePictureUrl: ""
                      }
                  },
            authProviderConfig: {
                type: "webAadWithMsaFed",

                // Used when the user is signed out and clicks sign in
                appSignInUrl: data.authProviderConfig.signInUrl,

                // Used when the user is signed out and has selected from a list the account they want to sign in with
                appSignInToUrl: data.authProviderConfig.signInToUrl,

                // The user clicks sign out. Should invoke a normal app sign out
                appSignOutUrl: data.currentAccount.isAnonymous ? "" : data.authProviderConfig.signOutUrl,

                // Used when the user is signed in and wants to switch to another account but didn't pick one from the list
                appSwitchUrl: data.currentAccount.isAnonymous ? "" : data.authProviderConfig.switchUrl,

                // Used when the user is signed in and chose an account from a list to switch to
                // Should handle sending the user to the login page with the given sign in name
                // The requested sign in name will be given in the "login_hint" parameter
                appSwitchToUrl: data.currentAccount.isAnonymous ? "" : data.authProviderConfig.switchToUrl,

                // AAD IDP Urls
                aad: {
                    signOutUrl: data.currentAccount.isAnonymous ? "" : data.aadIdpConfig.aadIdpSignOutUrl,
                    signOutAndForgetUrl: data.currentAccount.isAnonymous ? "" : data.aadIdpConfig.aadIdpSignOutAndForgetUrl,
                    forgetUrl: data.currentAccount.isAnonymous ? "" : data.aadIdpConfig.aadIdpForgetUrl,
                    rememberedAccountsUrl: data.aadIdpConfig.aadIdpRememberedAccountsUrl
                }
            },
            primaryUXConfig: data.currentAccount.isAnonymous
                ? ""
                : {
                      displayModes: [
                          {
                              type: "compressed",
                              widthBreakpoint: 5000
                          }
                      ],
                      theme: {
                          type: currentTheme.isDark ? "dark" : "light"
                      }
                  },
            appContextConfig: data.currentAccount.isAnonymous
                ? ""
                : {
                      commands: this.getCommands(data)
                  }
        };
    }

    private getCommands(data: IMeControlData) {
        const commands = [];
        if (!isFeatureFlagEnabled(this.pageContext, "VisualStudio.Services.AdminEngagement.MeControl.MeControlTenantPicker", false)) {
            commands.push({
                text: Resources.AzureDevOpsProfile,
                id: "userSettingsLink",
                ariaLabel: Resources.AzureDevOpsProfile,
                openInNewTab: true,
                url: data.profileUrl,
                type: "link"
            });
        }

        if (isFeatureFlagEnabled(this.pageContext, "VisualStudio.Services.AdminEngagement.MeControl.MeControlTenantPicker", false)) {
            commands.push({
                text: Resources.TenantPicker,
                id: "tenantPickerLink",
                ariaLabel: Resources.TenantPicker,
                openInNewTab: false,
                type: "link",
                onClick: this._showChangeTenants
            });
        }

        commands.push({
            text: Resources.Help,
            id: "showHelp",
            ariaLabel: Resources.Help,
            openInNewTab: false,
            type: "link",
            onClick: this._showHelp
        });

        commands.push({
            text: Resources.UserSettings,
            id: "showUserSettings",
            ariaLabel: Resources.UserSettings,
            openInNewTab: false,
            type: "link",
            onClick: this._showUserSettings
        });
        return commands;
    }

    private _showChangeTenants = () => {
        this._initializeTelemetry();
        this.telemetryService.publishEvent("MeControl", "TenantPicker", { Action: "Show Tenant Picker" });
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const openTenantPickerPanel: IContributedCommand = {
            methodName: "show",
            serviceName: "ITenantPickerPanel",
            dependencies: ["ms.vss-tfs-web.tenant-picker-content"]
        };
        contributionService.executeCommandEx("change-tenants", openTenantPickerPanel);
    };

    private _showUserSettings = () => {
        this._initializeTelemetry();
        this.telemetryService.publishEvent("MeControl", "UserSettings", { Action: "Show User Settings" });
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const openUserSetingsMenu: IContributedCommand = {
            methodName: "expandUserSettingsMenu",
            serviceName: "IUserSettingsMenu",
            dependencies: []
        };
        contributionService.executeCommandEx("show-user-settings-header-menu", openUserSetingsMenu);
    };

    private _showHelp = () => {
        this._initializeTelemetry();
        this.telemetryService.publishEvent("MeControl", "HelpMenu", { Action: "Show Help Menu" });
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const openUserSetingsMenu: IContributedCommand = {
            methodName: "expandHelpMenu",
            serviceName: "IUserSettingsMenu",
            dependencies: []
        };
        contributionService.executeCommandEx("show-help-header-menu", openUserSetingsMenu);
    };

    private _initializeTelemetry() {
        if (!this.telemetryService) {
            this.telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        }
    }
}

Services.add("IMeControlService", { serviceFactory: MeControlService });
